import { useTranslation } from 'react-i18next'
import { platformLogo, platformLogoFill } from '@/lib/platforms'
import { Badge } from '@rentflowdev/ui'
import type { PropertyPublication } from '@/types'


const MAX_VISIBLE = 2

interface PropertyPublicationBadgesProps {
	publications: PropertyPublication[]
}

export function PropertyPublicationBadges({ publications }: PropertyPublicationBadgesProps) {
	const { t } = useTranslation()

	// Grey is for absence. Anything actually published must not share it —
	// see the platform badge below.
	if (!publications.length) {
		return <Badge variant="muted">{t('properties.table.notPublished')}</Badge>
	}

	const visible = publications.slice(0, MAX_VISIBLE)
	const rest = publications.length - MAX_VISIBLE

	return (
		<div className="flex items-center gap-1">
			{visible.map((pub) => {
				// Здесь бейдж высотой в строку таблицы, и слово читается быстрее
				// значка. Марка сайта — тёмный квадрат: в таком размере она не
				// отличима от чужой, а приостановленное состояние, которое держится
				// на обесцвечивании, на ней просто не видно. Логотипы площадок
				// узнаются и мелкими, поэтому им значок оставлен.
				const logo = pub.platform === 'SITE' ? undefined : platformLogo(pub.platform)
				const label = t(`platforms.${pub.platform}`, { defaultValue: pub.platform })
				// A paused listing still counts as a publication — it is only
				// temporarily off the platform — so it keeps its badge, but it must
				// not read as live. It used to differ by opacity alone, which on the
				// grey text badge was invisible: pausing looked like nothing had
				// happened. Amber is the column's own «временно» — the platform name
				// still tells it apart from «Нет», which carries no name at all.
				const paused = pub.status === 'PAUSED'
				const title = paused
					? `${label} — ${t('properties.card.publications.statuses.PAUSED')}`
					: label
				const dimmed = paused ? 'grayscale' : undefined

				return logo ? (
					<Badge
						key={pub.platform}
						variant="logo"
						logoSrc={logo}
						logoAlt={title}
						logoFill={platformLogoFill(pub.platform)}
						className={dimmed}
						title={title}
					/>
				) : (
					// A platform with no logo — SITE today. It used to be `muted`,
					// the same grey as «Нет», so a published object and an
					// unpublished one read identically in the column. Blue is the
					// one semantic colour free here: green already means «Свободно»
					// in the status column two cells to the left.
					<Badge key={pub.platform} variant={paused ? 'warning' : 'info'} title={title}>{label}</Badge>
				)
			})}
			{rest > 0 && (
				<Badge variant="muted">+{rest}</Badge>
			)}
		</div>
	)
}
